import { useState, useEffect } from 'react'
import { Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import 'react-datepicker/dist/react-datepicker.css'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useNavigate } from 'react-router-dom'
import { KTSVG } from 'src/_metronic/helpers'
import { getParties } from '../../core/_requests'

const TransactionsListHeader = () => {
  const today = new Date()
  const [resetTriggered, setResetTriggered] = useState(false)
  const [filtersActive, setFiltersActive] = useState(false)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [parties, setParties] = useState<any[]>([])
  const [selectedSender, setSelectedSender] = useState<any>(null)
  const [selectedReceiver, setSelectedReceiver] = useState<any>(null)
  const [minAmount, setMinAmount] = useState('')
  const [maxAmount, setMaxAmount] = useState('')
  const [amountError, setAmountError] = useState('')
  const [filterFromDate, setFilterFromDate] = useState<Date | null>(today)
  const [filterToDate, setFilterToDate] = useState<Date | null>(today)
  const [appliedFilters, setAppliedFilters] = useState<any>({})
  const { state, updateState } = useQueryRequest()
  const navigate = useNavigate()

  useEffect(() => {
    const todayDate = new Date()
    const localTodayDate = new Date(todayDate.getTime() - todayDate.getTimezoneOffset() * 60000)
    const todayDateString = localTodayDate.toISOString().split('T')[0]
    
    updateState({
      filter: {
        'filter[fromDate]': todayDateString,
        'filter[toDate]': todayDateString,
      },
      page: 1,
      perPage: state.perPage,
    })
  }, [])

  useEffect(() => {
    if (showFilterModal) {
      fetchParties()
    }
  }, [showFilterModal])

  const fetchParties = async () => {
    try {
      const response = await getParties()
      const formattedParties = response.data.map((party: any) => ({
        value: party.partyId,
        label: party.nameCode,
        ...party
      }))
      setParties(formattedParties)
    } catch (error) {
      console.error('Error fetching parties:', error)
    }
  }

  const handleMinAmountChange = (value: string) => {
    if (value.length > 12) value = value.slice(0, 12)
    const regex = /^-?\d*$/
    if (regex.test(value)) {
      setMinAmount(value)
      // Clear error if max amount is empty or if min amount is now valid
      if (!maxAmount || (Number(value) <= Number(maxAmount))) {
        setAmountError('')
      } else {
        setAmountError('From Amount cannot be greater than To Amount')
      }
    }
  }

  const handleMaxAmountChange = (value: string) => {
    if (value.length > 12) value = value.slice(0, 12)
    const regex = /^-?\d*$/
    if (regex.test(value)) {
      setMaxAmount(value)
      // Clear error if min amount is empty or if max amount is now valid
      if (!minAmount || (Number(value) >= Number(minAmount))) {
        setAmountError('')
      } else {
        setAmountError('To Amount cannot be less than From Amount')
      }
    }
  }

  const handleSearch = () => {
    // Validate amounts before proceeding
    if (minAmount && maxAmount && Number(minAmount) > Number(maxAmount)) {
      setAmountError('From Amount cannot be greater than To Amount')
      return
    }

    const filterPayload: any = {}

    if (filterFromDate) {
      const localFromDate = new Date(filterFromDate.getTime() - filterFromDate.getTimezoneOffset() * 60000)
      filterPayload['filter[fromDate]'] = localFromDate.toISOString().split('T')[0]
    }
    if (filterToDate) {
      const localToDate = new Date(filterToDate.getTime() - filterToDate.getTimezoneOffset() * 60000)
      filterPayload['filter[toDate]'] = localToDate.toISOString().split('T')[0]
    }
    if (selectedSender) {
      filterPayload['filter[sender]'] = selectedSender.value
    }
    if (selectedReceiver) {
      filterPayload['filter[receiver]'] = selectedReceiver.value
    }
    if (minAmount) {
      filterPayload['filter[minAmount]'] = minAmount
    }
    if (maxAmount) {
      filterPayload['filter[maxAmount]'] = maxAmount
    }

    // Store the applied filters
    setAppliedFilters(filterPayload)
    
    updateState({
      filter: filterPayload,
      page: 1,
      perPage: state.perPage,
    })
    setShowFilterModal(false)
  }

  const handleReset = () => {
    const todayDate = new Date()
    const localTodayDate = new Date(todayDate.getTime() - todayDate.getTimezoneOffset() * 60000)
    const todayDateString = localTodayDate.toISOString().split('T')[0]

    setSelectedSender(null)
    setSelectedReceiver(null)
    setMinAmount('')
    setMaxAmount('')
    setAmountError('')
    setFilterFromDate(todayDate)
    setFilterToDate(todayDate)
    setResetTriggered(true)
    setAppliedFilters({})

    // Reset to today's date
    updateState({
      filter: {
        'filter[fromDate]': todayDateString,
        'filter[toDate]': todayDateString,
      },
      page: 1,
      perPage: state.perPage,
    })
  }

  useEffect(() => {
    if (resetTriggered) {
      handleSearch()
      setResetTriggered(false)
    }
  }, [resetTriggered])

  useEffect(() => {
    // Only show filters active if there are actually applied filters
    const isFilterActive = Boolean(
      Object.keys(appliedFilters).length > 0 &&
      (
        (appliedFilters['filter[fromDate]'] && appliedFilters['filter[fromDate]'] !== new Date().toISOString().split('T')[0]) ||
        (appliedFilters['filter[toDate]'] && appliedFilters['filter[toDate]'] !== new Date().toISOString().split('T')[0]) ||
        appliedFilters['filter[sender]'] ||
        appliedFilters['filter[receiver]'] ||
        appliedFilters['filter[minAmount]'] ||
        appliedFilters['filter[maxAmount]']
      )
    )
    setFiltersActive(isFilterActive)
  }, [appliedFilters])

  // Get filtered options for sender dropdown (excluding selected receiver)
  const getSenderOptions = () => {
    if (!selectedReceiver) return parties;
    return parties.filter(party => party.value !== selectedReceiver.value);
  }

  // Get filtered options for receiver dropdown (excluding selected sender)
  const getReceiverOptions = () => {
    if (!selectedSender) return parties;
    return parties.filter(party => party.value !== selectedSender.value);
  }

  return (
    <div className='card-header border-0 pt-6'>
      <div className='d-flex justify-content-between align-items-center w-100 position-relative'>
        <div className='d-flex flex-column flex-grow-1'>
          <div className='d-flex align-items-center position-relative flex-grow-1'>
            <h2 className='mb-2 flex-grow-1'>Transactions Report</h2>
            <div
              className="btn btn-icon btn-shadow-lg bg-primary text-white position-absolute start-100 translate-middle d-flex align-items-center justify-content-center border border-light"
              style={{ zIndex: 99, top: "50%", marginLeft: "-17px", width: "34px", height: "32px" }}
              role="button"
              aria-label="Go back to previous page"
              tabIndex={0}
              data-tooltip="Back"
              onClick={() => navigate(-1)}
            >
              <KTSVG path="/media/icons/duotune/arrows/arr025.svg" className="svg-icon-4 text-white" />
            </div>
          </div>
          <div className='d-flex align-items-center justify-content-end mt-2'>
            <button
                    type="button"
                    className="btn btn-light-primary"
                    onClick={() => setShowFilterModal(true)}
                  >
                    <KTSVG path="/media/icons/duotune/general/gen031.svg" className="svg-icon-2" />
                    Filter
                  </button>
           
            {filtersActive && (
              // <Button 
              //   variant='light' 
              //   className='btn-sm ms-2 px-3 py-2'
              //   onClick={handleReset}
              // >
              //   <i className='fa fa-times-circle me-2'></i>
              //   Reset
              // </Button>
              <button
              type="button"
              className="btn btn-light btn-active-light-primary fw-bold px-6 ms-2"
              onClick={handleReset}
              tabIndex={5}
            >
              Reset
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <Modal 
        show={showFilterModal} 
        onHide={() => setShowFilterModal(false)} 
        size="lg"
        dialogClassName="shadow-lg"
      >
        <Modal.Header closeButton className="py-3 border-bottom-2">
          <Modal.Title>
            <h3 className='m-0 d-flex align-items-center'>
              <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-2 me-2' />
              Filters
            </h3>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4 px-md-4">
          <div className="row g-4">
            <div className="col-12">
              <div className="filter-section">
                <h4 className="filter-title mb-3">
                  <KTSVG path='/media/icons/duotune/general/gen014.svg' className='svg-icon-3 me-2 text-primary' />
                  Date Range
                </h4>
                <div className="d-flex gap-3">
                  <div className="position-relative flex-grow-1">
                    <label className="form-label fw-bold fs-7 mb-2">From Date</label>
                    <DatePicker
                      selected={filterFromDate}
                      onChange={(date: Date | null) => setFilterFromDate(date)}
                      selectsStart
                      startDate={filterFromDate}
                      endDate={filterToDate}
                      dateFormat="dd-MM-yyyy"
                      className="form-control cursor-pointer"
                      placeholderText="Select start date"
                      maxDate={new Date()}
                    />
                    <i className="bi bi-calendar3 position-absolute" style={{ right: '15px', top: '67%', transform: 'translateY(-50%)', color: '#99A1B7', cursor: 'pointer' }}></i>
                  </div>
                  <div className="position-relative flex-grow-1">
                    <label className="form-label fw-bold fs-7 mb-2">To Date</label>
                    <DatePicker
                      selected={filterToDate}
                      onChange={(date: Date | null) => setFilterToDate(date)}
                      selectsEnd
                      startDate={filterFromDate}
                      endDate={filterToDate}
                      minDate={filterFromDate}
                      dateFormat="dd-MM-yyyy"
                      className="form-control cursor-pointer"
                      placeholderText="Select end date"
                      maxDate={new Date()}
                    />
                    <i className="bi bi-calendar3 position-absolute" style={{ right: '15px', top: '67%', transform: 'translateY(-50%)', color: '#99A1B7', cursor: 'pointer' }}></i>
                  </div>
                  {/* <i
                className='fa fa-calendar position-absolute'
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#555',
                }}
              ></i> */}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="filter-section">
                <h4 className="filter-title mb-3">
                  <KTSVG path='/media/icons/duotune/finance/fin001.svg' className='svg-icon-3 me-2 text-primary' />
                  Amount Range
                </h4>
                <div className="d-flex gap-3">
                  <div className="flex-grow-1">
                    <label className="form-label text-muted fw-bold fs-7 mb-2">From Amount</label>
                    <Form.Control
                      type="text"
                      placeholder="Enter from amount"
                      value={minAmount}
                      className={`form-control ${amountError ? 'is-invalid' : ''}`}
                      onChange={(e) => handleMinAmountChange(e.target.value)}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <label className="form-label text-muted fw-bold fs-7 mb-2">To Amount</label>
                    <Form.Control
                      type="text"
                      placeholder="Enter to amount"
                      value={maxAmount}
                      className={`form-control ${amountError ? 'is-invalid' : ''}`}
                      onChange={(e) => handleMaxAmountChange(e.target.value)}
                    />
                  </div>
                </div>
                {amountError && (
                  <div className="invalid-feedback d-block mt-2">
                    {amountError}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="filter-section h-100">
                <h4 className="filter-title mb-3">
                  <KTSVG path='/media/icons/duotune/communication/com013.svg' className='svg-icon-3 me-2 text-primary' />
                  Sender
                </h4>
                <Select
                  value={selectedSender}
                  onChange={setSelectedSender}
                  options={getSenderOptions()}
                  isClearable
                  placeholder="Select sender party"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      backgroundColor: '#f5f8fa',
                      border: '1px solid #e4e6ef',
                      cursor: 'pointer',
                      '&:hover': {
                        border: '1px solid #b5b5c3',
                      },
                    }),
                    option: (base) => ({
                      ...base,
                      cursor: 'pointer',
                    }),
                  }}
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="filter-section h-100">
                <h4 className="filter-title mb-3">
                  <KTSVG path='/media/icons/duotune/communication/com013.svg' className='svg-icon-3 me-2 text-primary' />
                  Receiver
                </h4>
                <Select
                  value={selectedReceiver}
                  onChange={setSelectedReceiver}
                  options={getReceiverOptions()}
                  isClearable
                  placeholder="Select receiver party"
                  className="react-select-container"
                  classNamePrefix="react-select"
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    return option.label.toLowerCase().startsWith(inputValue.toLowerCase());
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      backgroundColor: '#f5f8fa',
                      border: '1px solid #e4e6ef',
                      cursor: 'pointer',
                      '&:hover': {
                        border: '1px solid #b5b5c3',
                      },
                    }),
                    option: (base) => ({
                      ...base,
                      cursor: 'pointer',
                    }),
                  }}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-3 border-top-2">
          <Button 
            variant="primary" 
            className="px-6" 
            onClick={handleSearch}
          >
            Apply
          </Button>
          <Button 
            variant="light" 
            className="me-2 px-6" 
            onClick={() => {
              handleReset();
              setShowFilterModal(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .filter-section {
          background-color: #f5f8fa;
          border-radius: 0.65rem;
          padding: 1.5rem;
          border: 1px solid #e4e6ef;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .filter-title {
          font-size: 1rem;
          color: #181C32;
          display: flex;
          align-items: center;
        }
        .form-control-solid {
          background-color: #ffffff;
          border: 1px solid #e4e6ef;
          color: #181C32;
          transition: all 0.2s ease;
        }
        .form-control-solid:focus {
          background-color: #ffffff;
          border-color: #b5b5c3;
          box-shadow: none;
        }
        .form-control-solid.is-invalid {
          border-color: #f1416c;
          padding-right: calc(1.5em + 1.3rem);
          background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23f1416c'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23f1416c' stroke='none'/%3e%3c/svg%3e");
          background-repeat: no-repeat;
          background-position: right calc(0.375em + 0.325rem) center;
          background-size: calc(0.75em + 0.65rem) calc(0.75em + 0.65rem);
        }
        .invalid-feedback {
          color: #f1416c;
          font-size: 0.875rem;
        }
        .border-top-2 {
          border-top: 2px solid #f5f8fa !important;
        }
        .border-bottom-2 {
          border-bottom: 2px solid #f5f8fa !important;
        }
        .react-select-container .react-select__control {
          background-color: #ffffff !important;
        }
        .react-select-container .react-select__control:hover {
          border-color: #b5b5c3 !important;
        }
        .react-select-container .react-select__control--is-focused {
          border-color: #b5b5c3 !important;
          box-shadow: none !important;
        }
      `}</style>
    </div>
  )
}

export { TransactionsListHeader }
